export const MeetingImportStates = {
  ForImportReview: 'for_import_review',
  Imported: 'imported',
  Excluded: 'excluded'
} as const;

export type MeetingImportState =
  typeof MeetingImportStates[keyof typeof MeetingImportStates];

export const MeetingSources = {
  Teams: 'teams',
  InPerson: 'in_person'
} as const;

export type MeetingSource = typeof MeetingSources[keyof typeof MeetingSources];

export const MeetingListFilters = {
  All: 'all',
  Imported: 'imported',
  ForImportReview: 'for_import_review',
  Excluded: 'excluded'
} as const;

export type MeetingListFilter =
  typeof MeetingListFilters[keyof typeof MeetingListFilters];

export const MeetingStatuses = {
  InProgress: 'in_progress',
  Completed: 'completed',
  Failed: 'failed'
} as const;

export type MeetingStatus =
  typeof MeetingStatuses[keyof typeof MeetingStatuses];

export interface MeetingOwner {
  id: number;
  name: string;
  email: string | null;
  avatar_url: string | null;
}

export const MeetingListScopes = {
  My: 'my',
  All: 'all'
} as const;

export type MeetingListScope =
  typeof MeetingListScopes[keyof typeof MeetingListScopes];

export type MeetingListItem = {
  id: string;
  name: string;
  starts_at: string;
  participant_count: number;
  source: MeetingSource;
  import_state: MeetingImportState;
  status?: MeetingStatus;
  company_id?: number | null;
  company_name?: string | null;
  owner?: MeetingOwner | null;
  is_owner?: boolean;
  can_edit?: boolean;
};

export type MeetingUserMatch = {
  id: number;
  name: string;
  email?: string;
  telephone?: string | null;
  job_title?: string | null;
  job_title_id?: number | null;
  individual_id?: number | null;
};

export type MeetingInviteTag = {
  id: string;
  label: string;
  email?: string;
  type?: 'user' | 'contact';
  user_id?: number | null;
  user?: MeetingUserMatch | null;
  /** When set, selecting this tag auto-fills the speaker contact. */
  contact?: MeetingContactMatch | null;
};

export type MeetingContactMatch = {
  id: number | string;
  name: string;
  email?: string;
  company?: string;
  company_id?: number | null;
  companies?: MeetingCompanyDetail[];
  job_title?: string;
  job_title_id?: number;
  telephone?: string;
};

export type SpeakerAssignmentMatch =
  | {
      type: 'user';
      user: MeetingUserMatch;
    }
  | {
      type: 'contact';
      contact: MeetingContactMatch;
    };

export type MeetingCompanyDetail = {
  id: number;
  uuid?: string | null;
  name: string;
  website?: string | null;
  address?: string | null;
};

export type MeetingSpeakerRow = {
  id: string;
  display_name: string;
  talk_time_percent: number;
  affiliation: 'internal' | 'external';
  assignment_type?: 'user' | 'contact' | 'unassigned';
  assigned_user?: MeetingUserMatch | null;
  assigned_contact: MeetingContactMatch | null;
  /** Short transcript quote shown on assign cards. */
  quote_snippet?: string;
  /** Avatar shorthand e.g. S1 — derived from list index when omitted. */
  speaker_code?: string;
};

export type MeetingInsightFixture = {
  id: string;
  type:
    | 'buying_signal'
    | 'upsell_opportunity'
    | 'client_concern'
    | 'competitor_mention';
  label: string;
  description: string;
  /** Links insight chip / left-rail card to a transcript message. */
  transcriptMessageId?: string;
};

export type MeetingNoteFixture = {
  id: string;
  title: string;
  body: string;
};

export type MeetingTopicItem = {
  id: string;
  label: string;
  speakerId: string;
};

export type MeetingTranscriptMessage = {
  id: string;
  speakerId: string;
  timestamp: string;
  body: string;
  insightIds?: string[];
};

export type MeetingAttribution = {
  speaker: string | null;
  meeting_type: string | null;
  timestamp: string | null;
  formatted: string;
};

export type MeetingActionItemType =
  | 'draft_email'
  | 'create_deal'
  | 'create_task'
  | 'create_invoice'
  | 'create_quote'
  | 'create_reminder'
  | 'create_event';

export type MeetingActionItemFixture = {
  id: string;
  title: string;
  source: string;
  primaryAction: MeetingActionItemType;
  attribution?: MeetingAttribution;
};

import type {
  ABSAV3Response,
  AspectTimelineBlock,
  CallerSentimentDetail,
  EntityAspectSentimentDetail,
  ResolutionStatus,
  SentimentCategory,
  SpeakerStance
} from './sentiment-types';

export * from './sentiment-types';

export type MeetingReviewDetail = {
  meeting_id: string;
  duration_label: string;
  meeting_type: string;
  companies?: MeetingCompanyDetail[];
  company_name: string | null;
  company_id?: number | null;
  company?: MeetingCompanyDetail | null;
  invite_tags: MeetingInviteTag[];
  speakers: MeetingSpeakerRow[];
  linked_contact: MeetingContactMatch | null;
  insights: MeetingInsightFixture[];
  notes: MeetingNoteFixture[];
  recording_label: string;
  sentiment: {
    state: 'positive' | 'fair' | 'negative';
    points: number[];
    /** Annotation under the chart (e.g. dip description). */
    annotation?: string;
    /** Point indices that render dip markers. */
    markers?: number[];
  };
  /** Pre-computed or transformed aspect sentiment timeline blocks */
  sentiment_timeline?: AspectTimelineBlock[];
  /** Raw ABSA response if provided by sentiment_service */
  raw_absa_sentiment?: ABSAV3Response;
  /** Simple topic labels for review (CardViewMore). */
  topics: string[];
  summary_bullets?: string[] | null;
  transcript: string;
  /** ISO timestamp used in imported lockup subtitle. */
  imported_at?: string;
  call_purpose?: string;
  key_takeaways?: string[];
  /** Speaker-attributed topics for imported Call details Summary. */
  topic_items?: MeetingTopicItem[];
  transcript_messages?: MeetingTranscriptMessage[];
  action_items?: MeetingActionItemFixture[];
  resolved_action_items?: MeetingActionItemFixture[];
  unresolved_items?: string[];
  recording_url?: string | null;
};
